import {
  AlertTriangle,
  CalendarCheck,
  CheckCircle,
  Loader2,
  XCircle,
} from "lucide-react";
import { type JSX, useState } from "react";
import { useTranslation } from "react-i18next";
import { startMigrationNow } from "~/api/v2/requests";
import { type MicrosoftIntegration } from "~/api/v2/useMicrosoftIntegration";
import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from "~/atoms/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogTrigger,
} from "~/atoms/alert-dialog";
import { Button } from "~/atoms/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/atoms/card";

interface Props {
  data: MicrosoftIntegration;
  setupToken: string;
}

export function SpeSetupCard({ data, setupToken }: Props) {
  const { t } = useTranslation();
  const [isStarting, setIsStarting] = useState(false);

  const { migration, state } = data;

  let alertTitle: string;
  let alertIcon: JSX.Element;
  let showStartNow = false;

  if (migration.failedCount > 0) {
    alertTitle = t(($) => $.msIntegration.status.error);
    alertIcon = <XCircle />;
  } else if (state === "completed") {
    alertTitle = t(($) => $.msIntegration.status.completed);
    alertIcon = <CheckCircle />;
  } else if (state === "migration_in_progress") {
    alertTitle = `${t(($) => $.msIntegration.status.migrating)} (${migration.completedCount} / ${migration.totalCount})`;
    alertIcon = <Loader2 className="animate-spin" />;
  } else if (state === "pending_migration") {
    alertTitle = t(($) => $.msIntegration.status.waitingForMigration);
    alertIcon = <CalendarCheck />;
    showStartNow = true;
  } else {
    alertTitle = t(($) => $.msIntegration.status.creatingContainer);
    alertIcon = <Loader2 className="animate-spin" />;
  }

  const handleStartNow = async () => {
    setIsStarting(true);
    try {
      await startMigrationNow(setupToken);
    } catch {
      setIsStarting(false);
    }
  };

  return (
    <Card className="w-full max-w-xl">
      <CardHeader>
        <div className="flex items-center gap-3">
          <CardTitle>{t(($) => $.msIntegration.status.migrating)}</CardTitle>
        </div>
        <CardDescription>
          {t(($) => $.msIntegration.status.migratingDescription)}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Alert>
          {alertIcon}
          <AlertDescription>{alertTitle}</AlertDescription>
          {showStartNow && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <AlertAction>
                  <Button size="xs" variant="outline" disabled={isStarting}>
                    {t(($) => $.msIntegration.status.startNowButton)}
                  </Button>
                </AlertAction>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <Alert variant="warn">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>
                    {t(($) => $.msIntegration.status.startNowTitle)}
                  </AlertTitle>
                  <AlertDescription>
                    {t(($) => $.msIntegration.status.startNowWarning)}
                  </AlertDescription>
                </Alert>
                <AlertDialogFooter>
                  <AlertDialogCancel>
                    {t(($) => $.commons.buttons.cancel)}
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleStartNow}
                    disabled={isStarting}
                  >
                    {t(($) => $.msIntegration.status.startNowButton)}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </Alert>

        {migration && migration.failedCount > 0 && (
          <p>
            {migration.failedCount} {t(($) => $.msIntegration.status.failed)}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
